import { useMemo, useState } from "react";
import { CalendarDays, CreditCard, GitCompareArrows, Globe, Hotel, ListFilter, Utensils } from "lucide-react";
import FilterBar from "../components/controls/FilterBar";
import MultiSelectField from "../components/controls/MultiSelectField";
import Topbar from "../components/Topbar";
import ObjectSelectCard from "./analyticsDashboard/components/ObjectSelectCard";
import DiffDateTablesCard from "./analyticsDashboard/components/DiffDateTablesCard";
import { CARD_CLASS, FILTER_SELECT_CLASS } from "./analyticsDashboard/styles";
import {
  TEST_DIFF_BOARD_OPTIONS,
  TEST_DIFF_DASHBOARDS,
  TEST_DIFF_DAYS_STAY_OPTIONS,
  TEST_DIFF_OBJECTS,
  TEST_DIFF_OTA_OPTIONS,
  TEST_DIFF_PAYMENT_OPTIONS,
} from "./analyticsDashboard/testDataDiff";

const getIsoDateWithOffset = (offset: number) => {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  return date.toISOString().slice(0, 10);
};

export default function AnalyticsDashboardDiffTestPage() {
  const [selectedObject, setSelectedObject] = useState(TEST_DIFF_OBJECTS[0]?.value ?? "");
  const [filters, setFilters] = useState({
    dateFrom: "2026-03-20",
    dateTo: "2026-04-04",
    ota: [] as string[],
    boardType: [] as string[],
    paymentType: [] as string[],
    datePast: getIsoDateWithOffset(-7),
    hotel: [] as string[],
    daysStay: "1",
  });
  const [appliedAt, setAppliedAt] = useState<string | null>(null);

  const selectedDashboard = useMemo(
    () => TEST_DIFF_DASHBOARDS.find((item) => item.objectId === selectedObject) ?? TEST_DIFF_DASHBOARDS[0],
    [selectedObject],
  );

  const compareDateMax = getIsoDateWithOffset(-1);
  const inputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-800 shadow-sm focus:border-slate-300 focus:outline-none focus:ring-4 focus:ring-indigo-200/60";
  const hotelOptions = useMemo(
    () => selectedDashboard.rows.map((row) => ({ label: row.hotelName, value: row.hotelName })),
    [selectedDashboard],
  );
  const visibleRows = useMemo(() => {
    if (!filters.hotel.length) return selectedDashboard.rows;
    const selectedSet = new Set(filters.hotel);
    return selectedDashboard.rows.filter((row) => selectedSet.has(row.hotelName));
  }, [filters.hotel, selectedDashboard]);

  const handleApply = () => {
    setFilters((current) => ({
      ...current,
      datePast: current.datePast >= compareDateMax ? compareDateMax : current.datePast,
    }));
    setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
  };

  const handleReset = () => {
    setFilters({
      dateFrom: "2026-03-20",
      dateTo: "2026-04-04",
      ota: [],
      boardType: [],
      paymentType: [],
      datePast: getIsoDateWithOffset(-7),
      hotel: [],
      daysStay: "1",
    });
    setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="px-6 py-8">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-6">
          <section className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="pointer-events-none absolute inset-y-0 right-0 w-56 translate-x-10 rounded-full bg-gradient-to-b from-indigo-100 via-violet-100 to-white blur-3xl opacity-80" />
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm font-semibold text-indigo-600">BI-дашборд · тест</p>
                <h1 className="mt-2 text-3xl font-bold text-slate-900">Разница с ценой по дате</h1>
                <p className="mt-2 max-w-3xl text-sm text-slate-500">
                  Тестовый сценарий для проверки минимальных цен по датам и дельты к прошлой дате сравнения.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex items-center gap-3 self-start sm:self-auto">
                  <span className="text-xs font-semibold uppercase tracking-[0.08em] text-indigo-500">Срок заселения</span>
                  <div className="inline-flex rounded-full border border-indigo-200 bg-white p-1 shadow-sm">
                    {TEST_DIFF_DAYS_STAY_OPTIONS.map((option) => {
                      const isActive = filters.daysStay === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setFilters((current) => ({ ...current, daysStay: option.value }))}
                          className={[
                            "rounded-full px-3 py-1 text-xs font-semibold transition",
                            isActive ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-indigo-50",
                          ].join(" ")}
                          aria-pressed={isActive}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-5 py-4 text-sm text-indigo-700">
                  {appliedAt ? `Параметры обновлены в ${appliedAt}` : "Выберите объект и даты, затем нажмите «Применить»"}
                </div>
              </div>
            </div>
          </section>

          <ObjectSelectCard
            value={selectedObject}
            options={TEST_DIFF_OBJECTS}
            onChange={setSelectedObject}
          />

          <section className={CARD_CLASS}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                  <ListFilter size={18} />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Фильтры</p>
                  <p className="mt-2 text-sm text-slate-500">Настройте период, канал, питание, оплату и сравнение по прошлой дате</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleApply}
                  className="rounded-2xl border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-[0_10px_22px_rgba(99,102,241,0.15)] transition hover:bg-indigo-50"
                >
                  Применить
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                >
                  Сбросить
                </button>
              </div>
            </div>

            <FilterBar
              onApply={undefined}
              onReset={undefined}
              hasActive
              sticky={false}
              className="!mt-4 !border-0 !bg-transparent !p-0 !shadow-none"
            >
              <div className="min-w-[220px] flex-1">
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                  <CalendarDays size={18} className="text-slate-400" />
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(event) => setFilters((current) => ({ ...current, dateFrom: event.target.value }))}
                    className={inputClass}
                    aria-label="Дата заселения от"
                  />
                </div>
                <p className="mt-2 text-xs text-slate-400">Дата заселения от</p>
              </div>

              <div className="min-w-[220px] flex-1">
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                  <CalendarDays size={18} className="text-slate-400" />
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(event) => setFilters((current) => ({ ...current, dateTo: event.target.value }))}
                    className={inputClass}
                    aria-label="Дата заселения до"
                  />
                </div>
                <p className="mt-2 text-xs text-slate-400">Дата заселения до</p>
              </div>

              <div className="min-w-[220px] flex-1">
                <MultiSelectField
                  value={filters.ota}
                  onChange={(value) => setFilters((current) => ({ ...current, ota: value }))}
                  placeholder="OTA"
                  icon={<Globe size={18} />}
                  options={TEST_DIFF_OTA_OPTIONS}
                  className={FILTER_SELECT_CLASS}
                />
              </div>

              <div className="min-w-[220px] flex-1">
                <MultiSelectField
                  value={filters.boardType}
                  onChange={(value) => setFilters((current) => ({ ...current, boardType: value }))}
                  placeholder="Питание"
                  icon={<Utensils size={18} />}
                  options={TEST_DIFF_BOARD_OPTIONS}
                  className={FILTER_SELECT_CLASS}
                />
              </div>

              <div className="min-w-[220px] flex-1">
                <MultiSelectField
                  value={filters.paymentType}
                  onChange={(value) => setFilters((current) => ({ ...current, paymentType: value }))}
                  placeholder="Тип оплаты"
                  icon={<CreditCard size={18} />}
                  options={TEST_DIFF_PAYMENT_OPTIONS}
                  className={FILTER_SELECT_CLASS}
                />
              </div>

              <div className="min-w-[220px] flex-1">
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                  <GitCompareArrows size={18} className="text-slate-400" />
                  <input
                    type="date"
                    max={compareDateMax}
                    value={filters.datePast}
                    onChange={(event) => setFilters((current) => ({ ...current, datePast: event.target.value }))}
                    className={inputClass}
                    aria-label="Дата Парсинга"
                  />
                </div>
                <p className="mt-2 text-xs text-slate-400">Дата Парсинга</p>
              </div>

              <div className="min-w-[260px] flex-1">
                <MultiSelectField
                  value={filters.hotel}
                  onChange={(value) => setFilters((current) => ({ ...current, hotel: value }))}
                  placeholder="Отели"
                  icon={<Hotel size={18} />}
                  options={hotelOptions}
                  className={FILTER_SELECT_CLASS}
                />
              </div>
            </FilterBar>
          </section>

          <DiffDateTablesCard
            title={selectedDashboard.objectName}
            subtitle={`Дата заселения: ${filters.dateFrom} - ${filters.dateTo} · Дата Парсинга: ${filters.datePast} · Мин. stay: ${filters.daysStay}`}
            columns={selectedDashboard.columns}
            rows={visibleRows}
          />
        </div>
      </main>
    </div>
  );
}
